/**
 * Idempotency-Key for write requests. The server (IdempotencyInterceptor) runs a write once per key and answers a repeat
 * of the same key with the first answer. The key is made from the request itself plus a short time window, so a double
 * tap, or a retry after a slow network, sends the same key and can no longer book, charge or notify twice, while the same
 * action done again a moment later is a new one. Uploads get a random key (their body cannot be compared), and chat
 * messages none: sending the same words twice is allowed.
 */
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const KEY_WINDOW_MS = 5000;
/** The key given to each recent request, so a repeat of it keeps the same key for KEY_WINDOW_MS counted from the FIRST one
 *  (fixed clock buckets split a double tap that straddled a bucket edge into two different keys, and ran it twice). */
const recentKeys = new Map<string, { key: string; at: number }>();
let counter = 0;
const NO_KEY_URL = /\/(messages|conversations)(\/|\?|$)/;

function hash(text: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b + c, 0x85ebca6b) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

/** The key for this request, or null when it is not a write (or must not be deduplicated). */
export function idempotencyKey(method: string | undefined, url: string, body: unknown): string | null {
  const verb = (method ?? 'GET').toUpperCase();
  if (!WRITE_METHODS.has(verb) || NO_KEY_URL.test(url)) return null;
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
  const signature = hash(`${verb}|${url}|${typeof body === 'string' ? body : JSON.stringify(body ?? null)}`);
  const now = Date.now();
  const found = recentKeys.get(signature);
  if (found && now - found.at < KEY_WINDOW_MS) return found.key;
  if (recentKeys.size > 200) for (const [k, v] of recentKeys) if (now - v.at >= KEY_WINDOW_MS) recentKeys.delete(k);
  const key = `${signature}-${now.toString(36)}${(counter++).toString(36)}`;
  recentKeys.set(signature, { key, at: now });
  return key;
}

/** True for the answer to a repeat of a write that is still running: wait a moment and ask again for the first one's answer. */
export function isStillRunning(status: number | string | undefined, data: unknown): boolean {
  return status === 409 && /still being processed/i.test(typeof data === 'string' ? data : JSON.stringify(data ?? ''));
}

export const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
